import {IconButton} from '@dropbox/dig-components/buttons';
import {Menu} from '@dropbox/dig-components/menu';
import {ModalWidth} from '@dropbox/dig-components/modal';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {MoreHorizontalLine, PinFill, PinLine} from '@dropbox/dig-icons/assets';
import {DeleteModal} from 'components/DSYS/DeleteModal';
import {Facepile} from 'components/DSYS/Facepile';
import {PersonHoverCard} from 'components/DSYS/HoverCard';
import {ButtonLink, Link} from 'components/DSYS/Link';
import {Table} from 'components/DSYS/Table';
import {ROUTE_PATHS} from 'constant';
import {t} from 'i18next';
import {useCallback, useState} from 'react';
import {fromServerDate} from 'views/admin/utils';

import styles from './DocumentsTable.module.css';

type DeleteModalInfo = {
  documentId: string;
  useDeleteDoc: (documentId: string) => {mutateAsync: () => void; isPending: boolean};
  modalTitle: string;
  modalSubtitle: string;
  width?: ModalWidth;
};

export const DocumentNameCell = ({
  docName,
  docUrl,
  dbId,
  documentId,
  docOwner,
  docNameMaxWidth,
  showOverflowMenu,
  deleteModalInfo,
  onClick,
  isPinned,
  onTogglePin,
}: {
  docName: string | null | undefined;
  docUrl: string;
  dbId?: number;
  documentId?: string | null;
  docOwner: {
    ldap: string;
    name: string;
  };
  docNameMaxWidth?: number;
  showOverflowMenu?: boolean;
  deleteModalInfo?: DeleteModalInfo;
  onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
  isPinned?: boolean;
  onTogglePin?: () => void;
}) => (
  <Table.Cell className={styles.nameCell}>
    <Box display="flex" alignItems="flex-start" justifyContent="space-between">
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        onClick={onClick}
        style={docNameMaxWidth ? {maxWidth: `${docNameMaxWidth}px`} : undefined}
      >
        <Stack gap="8">
          <Box display="inline">
            <Link
              to={docUrl}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
              }}
              monochromatic
              hasNoUnderline
              showUnderlineOnHover
              isBold
            >
              <Text isBold>{docName ?? ''}</Text>
            </Link>
            {onTogglePin && (
              <Tooltip title={isPinned ? t('document_unpin_tooltip') : t('document_pin_tooltip')}>
                <IconButton
                  className={!isPinned ? styles.pinButton : undefined}
                  style={{marginLeft: 8}}
                  variant="borderless"
                  size="small"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    onTogglePin();
                  }}
                  aria-label={isPinned ? t('unpin') : t('pin')}
                >
                  <UIIcon
                    src={isPinned ? PinFill : PinLine}
                    className={atoms({color: isPinned ? 'Text Base' : 'Text Subtle'})}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {dbId && (
            <Box>
              <Box>
                <Text size="small">
                  {t('document_db_id_label')}: {dbId}
                </Text>
              </Box>
              {documentId && (
                <Box marginTop="4">
                  <Text size="small">
                    {t('document_id_label')}: {documentId}
                  </Text>
                </Box>
              )}
            </Box>
          )}

          <Box>
            <PersonHoverCard ldap={docOwner.ldap} name={docOwner.name}>
              <ButtonLink
                to={`${ROUTE_PATHS.PROFILE.replace(':userid', docOwner.ldap)}`}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.stopPropagation();
                }}
                variant="borderless"
                size="small"
                style={{display: 'inline-block', marginLeft: -5}}
                withIconStart={<Facepile ldaps={[docOwner.ldap]} size="xsmall" />}
              >
                <Text size="small" style={{marginLeft: '6px'}}>
                  {docOwner.name}
                </Text>
              </ButtonLink>
            </PersonHoverCard>
          </Box>
        </Stack>
      </Box>
      {deleteModalInfo && (
        <Box style={{visibility: showOverflowMenu ? 'visible' : 'hidden'}}>
          <DocumentOverflowMenu deleteModalInfo={deleteModalInfo} />
          <Box onClick={onClick} style={{height: '28px'}} />
        </Box>
      )}
    </Box>
  </Table.Cell>
);

export const DocumentDateCell = ({
  isoDate,
  title,
  onClick,
  children,
}: {
  isoDate: string;
  title?: string;
  onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
  children?: React.ReactNode;
}) => {
  const text = (
    <Text>
      {title}
      {new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(fromServerDate(isoDate))}
    </Text>
  );

  return (
    <Table.Cell onClick={onClick}>
      {children ? (
        <Box display="flex" flexDirection="column">
          {text}
          {children}
        </Box>
      ) : (
        text
      )}
    </Table.Cell>
  );
};

export const DocumentFacepilesCell = ({
  docOwnerLdap,
  ldaps,
  overflow,
  onClick,
}: {
  docOwnerLdap: string;
  ldaps: string[];
  overflow?: number;
  onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
}) => {
  return (
    <Table.Cell onClick={onClick}>
      <Text>
        <Facepile
          ldaps={[docOwnerLdap, ...ldaps]}
          size="small"
          overflow={overflow}
          ownerIsFirst={true}
        />
      </Text>
    </Table.Cell>
  );
};

export const DocumentOverflowMenu = ({deleteModalInfo}: {deleteModalInfo: DeleteModalInfo}) => {
  const {documentId, useDeleteDoc, modalTitle, modalSubtitle, width} = deleteModalInfo;
  const {mutateAsync: deleteReview, isPending} = useDeleteDoc(documentId);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = useCallback(async () => {
    await deleteReview();
  }, [deleteReview]);

  return (
    <>
      <Menu.Wrapper shouldPropagateClickOutsideMouseEvents={false}>
        {({getContentProps, getTriggerProps}) => {
          const triggerProps = getTriggerProps();
          return (
            <>
              <IconButton
                {...getTriggerProps()}
                variant="outline"
                className={atoms({display: 'block'})}
                size="small"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  triggerProps.onClick?.(e);
                }}
                aria-label="icon button"
              >
                <UIIcon src={MoreHorizontalLine} className={atoms({color: 'Text Subtle'})} />
              </IconButton>

              <Menu.Content {...getContentProps()} minWidth="200px" placement="bottom-end">
                <Menu.Segment>
                  <Menu.ActionItem
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      setShowDeleteModal(true);
                    }}
                  >
                    {t('delete')}
                  </Menu.ActionItem>
                </Menu.Segment>
              </Menu.Content>
            </>
          );
        }}
      </Menu.Wrapper>
      <DeleteModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={modalTitle}
        subtitle={modalSubtitle}
        isPending={isPending}
        onDelete={handleDelete}
        width={width}
      />
    </>
  );
};
